import ErrorBoundary from "@/common/ErrorBoundary";
import { MetaLayout } from "@/common/MetaLayout";
import { DatasetAsideContentSheetSkeleton } from "@/components/DatasetAsideContentSheet";
import { DatasetUseButton } from "@/components/DatasetUseButton";
import { DatasetVersionCardSkeleton } from "@/components/DatasetVersionCard";
import EntityProfilePic from "@/components/EntityProfilePic";
import NavItem from "@/components/NavItem";
import { ScrollableScaffoldNav } from "@/components/ScrollableScaffoldNav";
import VoteDisplay from "@/components/VoteDisplay";
import { EyeIcon, Lock01Icon } from "@/icons";
import { AsideContent, AsideRoot, AsideSection } from "@/kit/ui/aside";
import { Badge } from "@/kit/ui/badge";
import { Skeleton } from "@/kit/ui/skeleton";
import { Suspense } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Environment, graphql, useFragment } from "react-relay";
import {
  generatePath,
  Link,
  Outlet,
  ShouldRevalidateFunction,
} from "react-router";
import ErrorPage from "@/pages/_error";
import { DatasetScaffoldFragment$key } from "./__generated__/DatasetScaffoldFragment.graphql";
import { useLocation } from "@/utils/location";
import { HeaderContainer } from "@/components/HeaderContainer";
import { Container } from "@/components/Container";
import { invariant } from "@/utils/helpers";
import { useVersion, VersionContext } from "@/common/VersionContext";
import { VersionProvider } from "@/common/VersionProvider";
import VersionBadge from "@/components/VersionBadge";
import { DatasetVersionHistory } from "@/components/DatasetVersionHistory";
import { DatasetVersionHistoryFragment$key } from "@/components/DatasetVersionHistory/__generated__/DatasetVersionHistoryFragment.graphql";
import { useSignalValue } from "@/utils/signal";
import { getOgImageUrl, splitDatasetSlug } from "@/utils/routing";
import { safeParse } from "valibot";
import { LoaderArgs } from "@/utils/route";
import { preload, usePreloaded } from "@/utils/relay";
import { DatasetContentScaffoldParams } from "@/utils/dataset";
import { DatasetScaffoldQuery as DatasetScaffoldQueryType } from "./__generated__/DatasetScaffoldQuery.graphql";
import { DatasetScaffoldVersionQuery as DatasetScaffoldVersionQueryType } from "./__generated__/DatasetScaffoldVersionQuery.graphql";
import { isEqual } from "lodash";
export default function DatasetScaffold() {
  const intl = useIntl();
  const location = useLocation();
  const {
    query: { datasetBySlug, viewer },
  } = useDatasetScaffoldQuery();
  const dataset = useFragment(
    DatasetScaffoldFragment,
    datasetBySlug as DatasetScaffoldFragment$key,
  );
  if (!dataset) {
    return (
      <ErrorPage status={404}>
        <FormattedMessage defaultMessage="Dataset not found" />
      </ErrorPage>
    );
  }
  invariant(!!datasetBySlug, "datasetBySlug");
  const version =
    "version" in datasetBySlug
      ? datasetBySlug.version
      : datasetBySlug.latestVersion;
  return (
    <VersionProvider key={dataset.id} latestVersion={version?.version ?? null}>
      <MetaLayout
        metaTitle={
          viewer && viewer.viewerCanReadDatasetVersion
            ? intl.formatMessage(
                { defaultMessage: "{name} – Quantum Computing Dataset" },
                { name: dataset.name },
              )
            : intl.formatMessage({
                defaultMessage: "Quantum Computing Dataset",
              })
        }
        metaDescription={
          viewer && viewer.viewerCanReadDatasetVersion
            ? intl.formatMessage(
                {
                  defaultMessage:
                    "{name}{about}. A {visibility} dataset ready to download for algorithm development, QML workflows, and benchmarking.",
                },
                {
                  name: dataset.name,
                  visibility: dataset.private ? "private" : "public",
                  about:
                    dataset.shortDescription &&
                    dataset.shortDescription.length > 0
                      ? `: ${dataset.shortDescription}`
                      : "",
                },
              )
            : intl.formatMessage({
                defaultMessage:
                  "A dataset ready to download for algorithm development, QML workflows, and benchmarking.",
              })
        }
        metaImageUrl={getOgImageUrl(
          {
            type: "dataset",
            owner: dataset.owner.username,
            slug: dataset.localSlug,
          },
          location.origin,
        )}
        metaImageAlt={intl.formatMessage(
          { defaultMessage: "{name} on aqora" },
          { name: dataset.name },
        )}
      >
        <link rel="canonical" href={location.href} />
        <HeaderContainer>
          <div className="w-full flex md:flex-row flex-col md:items-center items-start gap-5 min-w-0">
            <div className="flex md:flex-row flex-col md:items-center items-start gap-2 min-w-0 flex-1">
              <div className="md:block hidden shrink-0">
                <Link
                  to={generatePath("/:username/datasets", {
                    username: dataset.owner.username,
                  })}
                >
                  <EntityProfilePic entity={dataset.owner} size="8" />
                </Link>
              </div>

              <div className="flex md:flex-row flex-col items-start gap-2 overflow-hidden">
                <p className="font-semibold text-2xl leading-8 text-grey-900 line-clamp-2 break-all">
                  <FormattedMessage
                    defaultMessage="<prev>{username} / </prev>{slug}"
                    values={{
                      username: dataset.owner.username,
                      slug: dataset.name,
                      prev: (chunks) => (
                        <span className="text-grey-500 whitespace-nowrap">
                          <Link
                            to={generatePath("/:username/datasets", {
                              username: dataset.owner.username,
                            })}
                            className="hover:underline"
                          >
                            {chunks}
                          </Link>
                        </span>
                      ),
                    }}
                  />
                </p>
                <div className="md:self-end self-start">
                  {dataset.private ? (
                    <Badge hasSuffix className="gap-1">
                      <Lock01Icon className="w-3 h-3" />
                      <FormattedMessage defaultMessage="Private" />
                    </Badge>
                  ) : (
                    <Badge hasSuffix className="gap-1">
                      <EyeIcon className="w-3 h-3" />
                      <FormattedMessage defaultMessage="Public" />
                    </Badge>
                  )}
                </div>
                <div className="md:self-end self-start">
                  {version && (
                    <VersionContext.Consumer>
                      {(context) => (
                        <VersionBadge
                          version={version.version}
                          onClick={(event) => {
                            event.preventDefault();
                            context?.openPanel.dispatch(true);
                          }}
                        />
                      )}
                    </VersionContext.Consumer>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 shrink-0 ml-auto">
              <VoteDisplay subject={dataset} kind="horizontal-upvote" />
              {version && <DatasetUseButton datasetVersion={version} />}
            </div>
          </div>

          <DatasetNavigator slug={dataset.slug} showSettings={!!viewer} />
        </HeaderContainer>
        <Container>
          <ErrorBoundary>
            <Suspense fallback={<DatasetChildPageSkeleton />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </Container>
      </MetaLayout>
      <DatasetVersionPanel dataset={datasetBySlug} />
    </VersionProvider>
  );
}
function DatasetVersionPanel({
  dataset,
}: {
  dataset: DatasetVersionHistoryFragment$key;
}) {
  const { openPanel } = useVersion();
  const open = useSignalValue(openPanel);
  return (
    <DatasetVersionHistory
      dataset={dataset}
      open={open}
      onOpenChange={(o) => openPanel.dispatch(o)}
    />
  );
}
function DatasetNavigator({
  slug,
  showSettings,
}: {
  slug: string;
  showSettings: boolean;
}) {
  const location = useLocation();
  const { selectedVersion } = useVersion();
  const datasetPath = (...rest: string[]) =>
    rest.length
      ? generatePath("/datasets/:owner/:slug/*", {
          ...splitDatasetSlug(slug),
          "*": rest.join("/"),
        })
      : generatePath("/datasets/:owner/:slug", splitDatasetSlug(slug));
  return (
    <ScrollableScaffoldNav className="-mb-px">
      <div className="flex justify-start items-center w-full">
        <NavItem
          end={
            location.pathname.includes("/discussions") ||
            location.pathname.includes("/settings")
          }
          to={
            selectedVersion
              ? datasetPath("versions", selectedVersion)
              : datasetPath()
          }
        >
          <FormattedMessage defaultMessage="Dataset" />
        </NavItem>
        <NavItem to={datasetPath("discussions")}>
          <FormattedMessage defaultMessage="Discussions" />
        </NavItem>
        {showSettings && (
          <NavItem to={datasetPath("settings")}>
            <FormattedMessage defaultMessage="Settings" />
          </NavItem>
        )}
      </div>
    </ScrollableScaffoldNav>
  );
}
function DatasetChildPageSkeleton() {
  return (
    <AsideRoot>
      <AsideContent>
        <DatasetVersionCardSkeleton />
        <DatasetAsideContentSheetSkeleton />
        <hr className="text-grey-200" />
        <DatasetAsideContentSheetSkeleton />
        <hr className="text-grey-200" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-16 rounded">
            <span />
          </Skeleton>

          <div className="flex flex-col items-start gap-2">
            <Skeleton className="h-4 w-36 rounded">
              <span />
            </Skeleton>
            <Skeleton className="h-4 w-28 rounded">
              <span />
            </Skeleton>
          </div>
        </div>
      </AsideContent>
      <AsideSection>
        <div className="flex sm:flex-row flex-col sm:items-center sm:justify-between gap-2">
          <Skeleton className="h-5 w-40 rounded">
            <span />
          </Skeleton>
          <Skeleton className="h-9 w-40 rounded-lg">
            <span />
          </Skeleton>
        </div>

        <Skeleton className="w-full h-64 rounded-lg">
          <span />
        </Skeleton>

        <Skeleton className="h-5 w-56 rounded">
          <span />
        </Skeleton>

        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-full rounded">
            <span />
          </Skeleton>
          <Skeleton className="h-4 w-5/6 rounded">
            <span />
          </Skeleton>
          <Skeleton className="h-4 w-2/3 rounded">
            <span />
          </Skeleton>
          <Skeleton className="h-4 w-3/4 rounded">
            <span />
          </Skeleton>
        </div>
      </AsideSection>
    </AsideRoot>
  );
}
const DatasetScaffoldFragment = graphql`
  fragment DatasetScaffoldFragment on Dataset {
    id
    slug
    localSlug
    name
    private
    shortDescription
    owner {
      username
      displayName
      ...EntityProfilePicFragment @arguments(thumbnail: true)
    }
    createdAt
    ...VoteDisplayFragment
  }
`;
const DatasetScaffoldQuery = graphql`
  query DatasetScaffoldQuery($owner: String!, $slug: String!) {
    viewer @ifAllowed {
      id
      viewerCanEdit: can(action: UPDATE_DATASET)
      viewerCanReadDatasetVersion: viewerCan(action: READ_DATASET_VERSION)
    }
    datasetBySlug(owner: $owner, localSlug: $slug) {
      ...DatasetScaffoldFragment
      ...DatasetVersionHistoryFragment
      latestVersion {
        version
        ...DatasetUseButtonFragment
      }
    }
  }
`;
const DatasetScaffoldVersionQuery = graphql`
  query DatasetScaffoldVersionQuery(
    $owner: String!
    $slug: String!
    $major: Int!
    $minor: Int!
    $patch: Int!
  ) {
    viewer @ifAllowed {
      id
      viewerCanEdit: can(action: UPDATE_DATASET)
      viewerCanReadDatasetVersion: viewerCan(action: READ_DATASET_VERSION)
    }
    datasetBySlug(owner: $owner, localSlug: $slug) {
      ...DatasetScaffoldFragment
      ...DatasetVersionHistoryFragment
      version(major: $major, minor: $minor, patch: $patch) {
        version
        ...DatasetUseButtonFragment
      }
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params }: LoaderArgs) => {
    const parse = safeParse(DatasetContentScaffoldParams, params);
    if (!parse.success) {
      throw new Response("Invalid request params", { status: 400 });
    }
    if ("version" in parse.output) {
      const {
        version: [major, minor, patch],
        ...rest
      } = parse.output;
      return preload<DatasetScaffoldVersionQueryType>(
        environment,
        DatasetScaffoldVersionQuery,
        {
          ...rest,
          major,
          minor,
          patch,
        },
      );
    } else {
      return preload<DatasetScaffoldQueryType>(
        environment,
        DatasetScaffoldQuery,
        parse.output,
      );
    }
  };
};
const useDatasetScaffoldQuery = () =>
  usePreloaded<DatasetScaffoldQueryType | DatasetScaffoldVersionQueryType>();

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
}) => !isEqual(currentParams, nextParams);
